import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { createRoot } from "react-dom/client"
import {
  LEVEL_UP_CELEBRATION_MS,
  playLevelUpCelebrationTone,
  runLevelUpCelebration,
} from "../../helpers/levelUpCelebration"
import { isOnboardingPath } from "../../helpers/routeAccess"

export const LEVEL_UP_CELEBRATION_EVENT = "amok:level-up"

const DURATION_MS = LEVEL_UP_CELEBRATION_MS + 200

let hostReady = false
let listenerReady = false
let pendingLevel = null

const dispatchCelebration = level => {
  window.dispatchEvent(
    new CustomEvent(LEVEL_UP_CELEBRATION_EVENT, {
      detail: { level: Math.max(1, Number(level) || 1) },
    }),
  )
}

/** Fire the level-up overlay after a real level increase (or preview). */
export const celebrateLevelUp = (level = 1, { silent = false } = {}) => {
  if (typeof window === "undefined" || typeof document === "undefined") return
  // Never animate or play fanfare during onboarding — callers queue for dashboard.
  if (isOnboardingPath(window.location.pathname)) return

  const nextLevel = Math.max(1, Number(level) || 1)
  // Unlock + play fanfare on the same user gesture when available
  if (!silent) {
    playLevelUpCelebrationTone()
  }
  ensureHost()

  // First call mounts the host asynchronously — queue until the listener is attached
  // so level 1→2 (and every later upgrade) still plays.
  if (!listenerReady) {
    pendingLevel = nextLevel
    return
  }

  dispatchCelebration(nextLevel)
}

const ensureHost = () => {
  if (hostReady) return
  let node = document.getElementById("amok-level-up-celebration-root")
  if (!node) {
    node = document.createElement("div")
    node.id = "amok-level-up-celebration-root"
    document.body.appendChild(node)
  }
  createRoot(node).render(<LevelUpCelebrationHost />)
  hostReady = true
}

const LevelUpCelebrationHost = () => {
  const [visible, setVisible] = useState(false)
  const [burstKey, setBurstKey] = useState(0)
  const [level, setLevel] = useState(1)
  const canvasRef = useRef(null)
  const cancelRef = useRef(null)

  useEffect(() => {
    const onCelebrate = event => {
      const nextLevel = Math.max(1, Number(event?.detail?.level) || 1)
      setLevel(nextLevel)
      setBurstKey(k => k + 1)
      setVisible(true)
    }

    window.addEventListener(LEVEL_UP_CELEBRATION_EVENT, onCelebrate)
    listenerReady = true

    if (pendingLevel != null) {
      const nextLevel = pendingLevel
      pendingLevel = null
      onCelebrate({ detail: { level: nextLevel } })
    }

    return () => {
      window.removeEventListener(LEVEL_UP_CELEBRATION_EVENT, onCelebrate)
      listenerReady = false
    }
  }, [])

  useEffect(() => {
    if (!visible) return undefined

    let cancel = null
    let safety = 0
    let startRaf = 0

    const start = () => {
      const canvas = canvasRef.current
      if (!canvas) {
        startRaf = requestAnimationFrame(start)
        return
      }
      cancelRef.current?.()
      cancel = runLevelUpCelebration(canvas, {
        onComplete: () => setVisible(false),
      })
      cancelRef.current = cancel
      safety = window.setTimeout(() => setVisible(false), DURATION_MS)
    }

    start()

    return () => {
      cancelAnimationFrame(startRaf)
      window.clearTimeout(safety)
      cancel?.()
      if (cancelRef.current === cancel) cancelRef.current = null
    }
  }, [visible, burstKey])

  if (!visible || typeof document === "undefined") return null

  return createPortal(
    <div
      key={burstKey}
      className="level-up-celebration"
      role="status"
      aria-live="polite"
      aria-label={`Congratulations! You've reached Level ${level}`}
    >
      <canvas
        ref={canvasRef}
        className="level-up-celebration__canvas"
        aria-hidden="true"
      />
      <div className="level-up-celebration__copy" aria-hidden="true">
        <span className="level-up-celebration__eyebrow">Level Up</span>
        <p className="level-up-celebration__message">Congratulations!</p>
        <p className="level-up-celebration__level">
          You&apos;ve reached <span>Level {level}</span>
        </p>
      </div>
    </div>,
    document.body,
  )
}

export default LevelUpCelebrationHost
